import { type FC, useEffect } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers.jsx';
import { getBuyerId } from 'dibs-cookie-jar';

import { NftFaq } from 'dibs-nft-faq/exports/NftFaq.tsx';

import { NftMarketGrid } from './components/NftMarketGrid.tsx';
import { useDeviceType } from '../nftHeader/hooks/useDeviceType.tsx';
import { type FilterShape } from './components/NftMarketSortAndFilter/filterConfig.ts';

import { type NftMarketLayout_viewer$key } from './__generated__/NftMarketLayout_viewer.graphql.ts';
import { type NftMarketLayoutRefetchQuery } from './__generated__/NftMarketLayoutRefetchQuery.graphql.ts';

import styles from './styles/NftMarketLayout.scss';

const messages = defineMessages({
    marketplaceHeading: {
        id: 'NftMarket.NftMarketLayout.marketplaceHeading',
        defaultMessage: 'All NFTs',
    },
});

export type NftMarketLayoutViewerKey = NftMarketLayout_viewer$key;
export const NftMarketLayout: FC<{
    viewer: NftMarketLayout_viewer$key | null | undefined;
    startingPage: number;
    startingFilters: FilterShape;
}> = ({ viewer: viewerProp, startingPage, startingFilters }) => {
    const { deviceType } = useDeviceType();
    const { formatMessage } = useIntl();
    const [viewer, refetch] = useRefetchableFragment<
        NftMarketLayoutRefetchQuery,
        NftMarketLayout_viewer$key
    >(
        graphql`
            fragment NftMarketLayout_viewer on Viewer
            @refetchable(queryName: "NftMarketLayoutRefetchQuery") {
                ...NftMarketGrid_viewer
                    @arguments(
                        after: $after
                        page: $page
                        auctionStatuses: $auctionStatuses
                        auctionMarketTypes: $auctionMarketTypes
                        mediumTypes: $mediumTypes
                        sortBy: $sortBy
                        search: $search
                    )
            }
        `,
        viewerProp
    );

    useEffect(() => {
        const buyerId = getBuyerId(document.cookie);
        if (buyerId) {
            refetch({ userId: buyerId, fetchUser: true });
        }
        addLoginHandlers(() => {
            const userId = getBuyerId(document.cookie);
            if (userId) {
                refetch({ userId: buyerId, fetchUser: true });
            }
        });
    }, [refetch]);

    return (
        <>
            <div className={styles.pageHeading}>
                <h1 className={styles.pageTitle}>{formatMessage(messages.marketplaceHeading)}</h1>
            </div>
            <NftMarketGrid
                viewer={viewer || null}
                startingPage={startingPage}
                startingFilters={startingFilters}
            />
            <NftFaq deviceType={deviceType} />
        </>
    );
};
